import type { AppRouter } from "@onadssuper/trpc";
import { httpBatchLink, httpLink, splitLink, type TRPCLink } from "@trpc/client";
import superjson from "superjson";

export const WORKSPACE_HEADER = "x-workspace-id";
export const IDEMPOTENCY_HEADER = "idempotency-key";

export interface ApiClientOptions {
  /** Full URL of the tRPC endpoint, e.g. "/api/v1/trpc" on web. */
  url: string;
  /** The workspace the current screen works in; sent on every call. */
  getWorkspaceId: () => string | undefined;
  /** Custom fetch (mobile/tests). Defaults to the global fetch with cookies included. */
  fetch?: typeof globalThis.fetch;
}

/**
 * Shared tRPC link chain for web (now) and mobile (Phase 6).
 *  - queries are batched;
 *  - each mutation is its own request carrying a fresh Idempotency-Key, so a
 *    network retry of the same call can never run twice on the server;
 *  - an identical mutation sent while the first is still on its way (a double
 *    click) shares the first one's response instead of doing the work again;
 *  - superjson keeps Dates as Dates.
 */
export const createApiClientLinks = (options: ApiClientOptions): TRPCLink<AppRouter>[] => {
  const baseFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const fetchWithCookies: typeof globalThis.fetch = (input, init) =>
    baseFetch(input, { ...init, credentials: "include" });

  const inFlight = new Map<string, Promise<Response>>();
  const sendMutationOnce: typeof globalThis.fetch = (input, init) => {
    const key = JSON.stringify([
      input instanceof Request ? input.url : input.toString(),
      init?.body,
      // The header this request actually carries: the workspace may have changed since (WLK-01).
      new Headers(init?.headers).get(WORKSPACE_HEADER),
    ]);
    let sent = inFlight.get(key);
    if (!sent) {
      sent = fetchWithCookies(input, init).finally(() => inFlight.delete(key));
      inFlight.set(key, sent);
    }
    return sent.then((response) => response.clone());
  };

  const workspaceHeaders = (): Record<string, string> => {
    const workspaceId = options.getWorkspaceId();
    return workspaceId ? { [WORKSPACE_HEADER]: workspaceId } : {};
  };

  return [
    splitLink({
      condition: (op) => op.type === "mutation",
      true: httpLink({
        url: options.url,
        transformer: superjson,
        fetch: sendMutationOnce,
        headers: () => ({ ...workspaceHeaders(), [IDEMPOTENCY_HEADER]: crypto.randomUUID() }),
      }),
      false: httpBatchLink({
        url: options.url,
        // Must not exceed the server's maxBatchSize (SEC-11).
        maxItems: 10,
        transformer: superjson,
        fetch: fetchWithCookies,
        headers: workspaceHeaders,
      }),
    }),
  ];
};
